import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardService, CurrentEventResponse, DashboardStatusResponse } from '../utils/dashboardService';

interface DashboardProps {
  className?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ className }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentEvent, setCurrentEvent] = useState<CurrentEventResponse | null>(null);
  const [dashboardStatus, setDashboardStatus] = useState<DashboardStatusResponse | null>(null);

  useEffect(() => {
    const initializeDashboard = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get dashboard status to understand user's role and context
        const status = await DashboardService.getDashboardStatus(token);
        setDashboardStatus(status);

        // Get current event info for dashboard display
        try {
          const event = await DashboardService.getCurrentEvent(token);
          setCurrentEvent(event);
        } catch (eventError: any) {
          // No active event is not an error - just means no current event to display
          if (eventError.message?.includes('No active event')) {
            setCurrentEvent(null);
          } else {
            throw eventError;
          }
        }

      } catch (err: any) {
        console.error('Dashboard initialization failed:', err);
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [token]);

  if (loading) {
    return (
      <div className={`dashboard ${className || ''}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dashboard ${className || ''}`}>
        <div className="error-container">
          <h2>❌ Dashboard Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Dashboard component - shows overview and quick actions for all user types
  return (
    <div className={`dashboard ${className || ''}`}>
      <div className="dashboard-header">
        <h1>🚀 Hackathon Manager Dashboard</h1>
        <p>Welcome, {user?.display_name || user?.email}</p>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <h2>📊 Current Status</h2>
          
          {dashboardStatus && (
            <div className="status-card">
              <div className="status-item">
                <strong>Your Role:</strong> 
                {dashboardStatus.is_organizer && <span className="role-badge organizer">🏛️ Organizer</span>}
                {dashboardStatus.is_judge && <span className="role-badge judge">⚖️ Judge</span>}
                {dashboardStatus.is_participant && <span className="role-badge participant">👤 Participant</span>}
              </div>
              
              <div className="status-item">
                <strong>Active Event:</strong> 
                {dashboardStatus.has_active_event ? (
                  <span className="status-active">
                    ✅ {dashboardStatus.active_event_name}
                  </span>
                ) : (
                  <span className="status-inactive">❌ No active event</span>
                )}
              </div>

              {dashboardStatus.recommended_action && (
                <div className="status-item">
                  <strong>Recommended Action:</strong>
                  <span className="recommended-action">{dashboardStatus.recommended_action}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <h2>⚡ Quick Actions</h2>
          <div className="quick-actions">
            {currentEvent ? (
              <>
                <button 
                  onClick={() => navigate(`/events/${currentEvent.event_id}`)}
                  className="btn btn-primary action-card"
                >
                  <div className="action-icon">🎯</div>
                  <div className="action-content">
                    <strong>Go to Current Event</strong>
                    <small>{currentEvent.name}</small>
                  </div>
                </button>
                
                {dashboardStatus?.is_organizer && (
                  <>
                    <button 
                      onClick={() => navigate(`/events/${currentEvent.event_id}/participants`)}
                      className="btn btn-secondary action-card"
                    >
                      <div className="action-icon">👥</div>
                      <div className="action-content">
                        <strong>View Participants</strong>
                        <small>Manage & export participant list</small>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/events/${currentEvent.event_id}/presentation`)}
                      className="btn btn-secondary action-card"
                    >
                      <div className="action-icon">🎥</div>
                      <div className="action-content">
                        <strong>Presentation View</strong>
                        <small>Teams overview & OBS data export</small>
                      </div>
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/events/${currentEvent.event_id}/aws-accounts`)}
                      className="btn btn-warning action-card"
                    >
                      <div className="action-icon">☁️</div>
                      <div className="action-content">
                        <strong>AWS Account Management</strong>
                        <small>Assign teams to hackathon accounts</small>
                      </div>
                    </button>
                  </>
                )}
              </>
            ) : (
              dashboardStatus?.is_organizer && (
                <button 
                  onClick={() => navigate('/create-event')}
                  className="btn btn-primary action-card"
                >
                  <div className="action-icon">➕</div>
                  <div className="action-content">
                    <strong>Create New Event</strong>
                    <small>Start a new hackathon</small>
                  </div>
                </button>
              )
            )}

            <button 
              onClick={() => navigate('/events')}
              className="btn btn-secondary action-card"
            >
              <div className="action-icon">📅</div>
              <div className="action-content">
                <strong>View All Events</strong>
                <small>Browse event history</small>
              </div>
            </button>

            <button 
              onClick={() => navigate('/teams')}
              className="btn btn-secondary action-card"
            >
              <div className="action-icon">👥</div>
              <div className="action-content">
                <strong>View Teams</strong>
                <small>Manage team participation</small>
              </div>
            </button>
          </div>
        </div>

        {currentEvent && (
          <div className="dashboard-section">
            <h2>📈 Current Event Overview</h2>
            <div className="event-overview">
              <div className="overview-item">
                <strong>Event:</strong> {currentEvent.name}
              </div>
              <div className="overview-item">
                <strong>Status:</strong> 
                <span className={`status-badge ${currentEvent.state}`}>
                  {currentEvent.state.toUpperCase()}
                </span>
              </div>
              <div className="overview-item">
                <strong>Teams:</strong> {currentEvent.team_count}
              </div>
              <div className="overview-item">
                <strong>Participants:</strong> {currentEvent.participant_count}
              </div>
              <div className="overview-item">
                <strong>Registration Deadline:</strong> 
                {new Date(currentEvent.registration_deadline).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
